import { Controller } from "@hotwired/stimulus"
import HighchartsV12 from "highcharts-v12"
import { highchartsSettingsV12 } from "@xlayer/composables/chartModules/highchartsSetup"


export const TAG_CLASSES = {
  trust: "border-trust-200 bg-trust-100/60 text-trust-700",
  passion: "border-passion/30 bg-passion/10 text-passion",
  happiness: "border-happiness/50 bg-happiness/15 text-clarity-800",
  change: "border-change/30 bg-change/10 text-change",
}
export const NEUTRAL_TAG = "border-gray-200 bg-neutrality text-clarity-700"

export default class extends Controller {
  static values = { data: String, filter: String, id: String, layout: String, basecheck: String, skipFinalPages: Boolean }

  connect() {
    highchartsSettingsV12()
    
    const data = JSON.parse(this.dataValue)
    
    
    const basecheck = this.hasBasecheckValue ? JSON.parse(this.basecheckValue) : null

    if (basecheck && this.idValue == "") {
      this.handleBasecheckComponent(basecheck)
      this.setBasecheckText(basecheck.warn)
    } 
    const sortedComponents = this.sortGridByPosition(JSON.parse(this.layoutValue))
    sortedComponents.forEach(component => {
      if (this.idValue !== "" && component.componentId !== this.idValue) {
        return
      }
      if (component.isGroup === true) {
        this.handleGroupComponent(component)
      } else {
        const componentData = data.find(componentData => componentData.id === component.componentId)
        if (!componentData) {
          console.log(`No data found for component with ID: ${component.componentId}`)
          return
        }
        
        switch (componentData.type) {
              case "chart":
                this.handleChartComponent(componentData)
                break
              case "score":
                this.handleScoreComponent(componentData)
                break
              case "map":
                this.handleMapComponent(componentData)
                break
              case "table":
                this.handleTableComponent(componentData)
                break
              case "tags":
                this.handleTagsComponent(componentData)
                break
              case "vacancies":
                this.handleVacanciesComponent(componentData)
                break
            }
      }
    })
    if (data.length > 1) {  
      this.addFinalPages()
    }
    this.waitForRenderingComplete(this.element)
  }
  
  addFinalPages() {
    if (this.skipFinalPagesValue) {
      return
    }

    const thanksTemplate = document.getElementById("hrm-thanks-template")?.content.cloneNode(true)
    if (thanksTemplate) {
      this.element.appendChild(thanksTemplate)
    }
    
    const contactTemplate = document.getElementById("hrm-contact-template")?.content.cloneNode(true)
    if (contactTemplate) {
      this.element.appendChild(contactTemplate)
    }
  }
  
 sortGridByPosition(gridArray) {
    return [...gridArray].sort((a, b) => {
      if (a.y !== b.y) {
        return a.y - b.y
      }
      return a.x - b.x
    });
  }
  
  disconnect() {
    if (this.vueApp) {
      this.vueApp.unmount();
    }
  }
  
  async handleGroupComponent(component) {
    const chartId = `chart-container-${component.componentId}`
    const componentData = JSON.parse(this.dataValue)
    const template = this.createComponentTemplate("hrm-group-template", chartId,
       componentData.find(componentData => componentData.id === component.groupLayout?.header)?.display)
  
    this.element.appendChild(template)
    
    const { createApp } = await import("vue")
    const { default: GroupWidget } = await import("@xlayer/widgets/GroupWidgetSimplified.vue")
    
    const app = createApp(GroupWidget, {
      modelValue: component,
      components: componentData,
      layout: JSON.parse(this.layoutValue)
    });
    const container = document.getElementById(chartId)
    app.mount(container);
    this.vueApp = app;
  }
  
  handleTableComponent(component) {
    const chartId = `chart-container-${component.id}`
    const template = this.createComponentTemplate("hrm-table-template", chartId, component.display)
    this.element.appendChild(template)
    this.renderTable(component)
  }
  
  handleTagsComponent(component) {
    const chartId = `chart-container-${component.id}`
    const template = this.createComponentTemplate("hrm-table-template", chartId, component.display)
    this.element.appendChild(template)

    const container = document.getElementById(chartId)
    if (!container) {
      console.error("Tags container not found:", chartId)
      return
    }

    if (component.description) {
      const description = document.createElement('p')
      description.className = 'mb-5 text-sm text-clarity-600'
      description.textContent = component.description
      container.appendChild(description)
    }

    const list = document.createElement('ul')
    list.className = 'flex flex-wrap gap-2'
    const tags = component.tags || []
    tags.forEach(tag => {
      const item = document.createElement('li')
      item.className = `rounded-full border px-3 py-1.5 text-sm ${TAG_CLASSES[component.color] ?? NEUTRAL_TAG}`
      item.textContent = tag
      list.appendChild(item)
    })
    container.appendChild(list)
  }

  handleVacanciesComponent(component) {
    const chartId = `chart-container-${component.id}`
    const template = this.createComponentTemplate("hrm-table-template", chartId, component.display)
    this.element.appendChild(template)

    const container = document.getElementById(chartId)
    if (!container) {
      console.error("Vacancies container not found:", chartId)
      return
    }

    const list = document.createElement('ul')
    list.className = 'divide-y divide-gray-100'
    const vacancies = (component.vacancies || []).slice(0, 10)
    vacancies.forEach(vacancy => {
      const item = document.createElement('li')
      item.className = 'py-2'
      const title = document.createElement('span')
      title.className = 'block font-semibold text-clarity'
      title.textContent = vacancy.title || ''
      const meta = document.createElement('span')
      meta.className = 'block text-sm text-clarity-500'
      meta.textContent = [vacancy.organization, vacancy.location, vacancy.region].filter(Boolean).join(' · ')
      item.append(title, meta)
      list.appendChild(item)
    })
    container.appendChild(list)
  }

  renderTable(component) {
    const chartId = `chart-container-${component.id}`
    const container = document.getElementById(chartId)
    if (!container) {
      console.error("Table container not found:", chartId)
      return
    }
    
    const table = document.createElement('table')
    table.className = 'w-full text-left border-collapse text-sm'
    
    const thead = document.createElement('thead')
    const headerRow = document.createElement('tr')
    
    if (component.tableData?.headers && Array.isArray(component.tableData.headers)) {
      component.tableData.headers.forEach(header => {
        const th = document.createElement('th')
        th.className = 'py-1 px-2 font-bold border-b-2 border-gray-300'
        th.textContent = header.name || ''
        if (header.width) {
          th.style.width = `${header.width}px`
        }
        headerRow.appendChild(th)
      })
      
      thead.appendChild(headerRow)
      table.appendChild(thead)
    }
    
    const tbody = document.createElement('tbody')
    
    if (component.tableData?.columns && Array.isArray(component.tableData.columns) && component.tableData.columns.length > 0) {
      const dataKeys = Object.keys(component.tableData.columns[0])
      
      component.tableData.columns.forEach((row, rowIndex) => {
        const tr = document.createElement('tr')
        tr.className = rowIndex % 2 === 0 ? 'bg-white' : 'bg-gray-50'
        
        dataKeys.forEach(key => {
          const td = document.createElement('td')
          td.className = 'py-1 px-2 border-b border-gray-200'
          td.textContent = row[key] !== undefined ? row[key] : ''
          tr.appendChild(td)
        })
        
        tbody.appendChild(tr)
      })
      
      table.appendChild(tbody)
    }
    
    container.appendChild(table)
  }

  handleBasecheckComponent(component) {
    const chartId = `chart-container-${component.id}`
    
    const display = {
      header: {
        title: component.highchartsData.title?.text || 'Anzahl Befragte(n)'
      }
    }
    
    const template = this.createComponentTemplate("hrm-chart-template", chartId, display)
    this.element.appendChild(template)
    
    const chartData = JSON.parse(JSON.stringify(component.highchartsData))
    
    if (chartData.title) {
      chartData.title.text = ''
      chartData.title.enabled = false
    }
    
    chartData.chart = chartData.chart || {}
    chartData.chart.showAxes = false
    
    if (!chartData.yAxis) {
      chartData.yAxis = []
    }
    
    if (Array.isArray(chartData.yAxis)) {
      chartData.yAxis.forEach(axis => {
        axis.visible = true       
        axis.gridLineWidth = 0
        axis.lineWidth = 0
        axis.tickWidth = 0

        if (axis.title && !axis.title.text.includes('*')) {
          axis.title.text = ''
        }
      })
    }
    
    this.renderChart(chartData, chartId)
  }


  handleChartComponent(component) {
    const chartId = `chart-container-${component.id}`
    const template = this.createComponentTemplate("hrm-chart-template", chartId, component.display)
    this.element.appendChild(template)
    this.renderChart(component.highchartsData, chartId)
  }

  handleScoreComponent(component) {
    const displayData = component.display ? JSON.parse(JSON.stringify(component.display)) : {}
    
    if ((!displayData?.header?.title || displayData.header.title === '') && 
        displayData?.header?.tooltip?.value) {
      displayData.header = displayData.header || {}
      
      const tempDiv = document.createElement('div')
      tempDiv.innerHTML = displayData.header.tooltip.value
      
      displayData.header.title = tempDiv.textContent.trim()
      displayData.header.title = tempDiv.textContent.trim()
    }
    
    const template = this.createComponentTemplate("hrm-score-template", null, displayData)
    this.populateScoreComponent(template, component)
    this.element.appendChild(template)
  }

  createComponentTemplate(templateId, chartId, display?) {
    const template = document.getElementById(templateId).content.cloneNode(true)
    if (chartId) {
      const chartContainer = template.querySelector(".chart-container")
      chartContainer.id = chartId
    }

    if (display?.header?.title) {
      const titleElement = template.querySelector(".title")
      titleElement.textContent = display.header.title
    }
    
    if (display?.header?.tooltip?.value) {
      const tempDiv = document.createElement('div')
      tempDiv.innerHTML = display.header.tooltip.value
      const tooltipText = tempDiv.textContent.trim()
      
      if (tooltipText !== display?.header?.title) {
        const tooltipElement = template.querySelector(".tooltip")
        
        const elements = tempDiv.getElementsByTagName('*')
        for (const element of elements) {
          element.removeAttribute('style')
          element.className = ''
        }
              
        if (elements.length === 0 || elements.length === 1) {
          tooltipElement.innerHTML = `<div class="mt-1">${tempDiv.innerHTML}</div>`
        } else {
          tooltipElement.innerHTML = `<div class="mt-1">${tempDiv.innerHTML}</div>`
        }
      }
    }
    
    if (this.filterValue !== undefined) {
      const filterElement = template.querySelector(".filter")
      const filterObject = JSON.parse(this.filterValue)
      filterElement.innerHTML = this.generateFilterHTML(filterObject)
    }

    return template
  }

  populateScoreComponent(template, component) {
    const scoreComponent = template.querySelector(".score-container")
    const imageElement = scoreComponent.querySelector(".image")
    const valueElement = scoreComponent.querySelector(".value")
    const compareValueElement = scoreComponent.querySelector(".compare-value")
    const deltaElement = scoreComponent.querySelector(".delta")
    const textElement = scoreComponent.querySelector(".text")

    if (component.image) {
      imageElement.src = component.image
    }

    const value = component.value
    valueElement.textContent = this.formatValue(value, component.unit, component.minimumFractionDigits, component.maximumFractionDigits)

    const valueCompare = component.valueCompare
    if (valueCompare !== value && valueCompare !== undefined) {
      compareValueElement.textContent = this.formatValue(valueCompare, component.unit, component.minimumFractionDigits, component.maximumFractionDigits, true)
    }

    if (component.delta) {
      deltaElement.innerHTML = this.formatDelta(component.delta)
    }

    if (component.text) {
      textElement.innerHTML = component.text
    }
    
    if (component.color) {
      valueElement.classList.add(`text-[${component.color}]`)
    }
  }

  formatValue(value, unit, minDigits, maxDigits, isCompare = false) {
    if (value == 0 || value == null || value === undefined) {
      return isCompare ? "(-)" : "(-)"
    }
    return `${value.toLocaleString('de-DE', {
      minimumFractionDigits: minDigits,
      maximumFractionDigits: maxDigits
    })} ${unit || ''}`
  }

  formatDelta(delta) {
    if (delta.value > 0) {
      return `&uarr; ${delta.label}`
    } else if (delta.value < 0) {
      return `&darr; ${delta.label}`
    } else {
      return `${delta.label}`
    }
  }

  generateFilterHTML(filterObject) {
    let html = ''
    for (const group in filterObject) {
      const filters = filterObject[group]
      const cleanedGroup = group.replace('Filter', '').trim()
      html += '<div class="flex items-center gap-2">'
      if (filters && filters.length > 0) {
        const colorClass = cleanedGroup.includes('Zielgruppe') 
          ? 'text-[#3366CC]' 
          : cleanedGroup.includes('Vergleichsgruppe') 
            ? 'text-gray-500'
            : ''
        html += `<b class="${colorClass}">${cleanedGroup}:</b>`
        html += `<span>${filters}</span>`
        html += '</div>'
      }
    }

    return html
  }

  async handleMapComponent(component) {
    const chartId = `chart-container-${component.id}`
    const template = this.createComponentTemplate("hrm-chart-template", chartId, component.display)
    this.element.appendChild(template)
    
    const mapOptions = JSON.parse(JSON.stringify(component.highchartsData))
    
    const { default: germanyMapData } = await import('@xlayer/composables/modules/germanyMap.topo.json')
      
    mapOptions.chart = mapOptions.chart || {}
    mapOptions.chart.map = germanyMapData
    
    mapOptions.mapNavigation = mapOptions.mapNavigation || {}
    mapOptions.mapNavigation.enabled = false
    
    this.renderChart(mapOptions, chartId, 'mapChart')
  }

  async renderChart(chartData, chartId, constructorType = 'chart') {
    const chartContainer = document.getElementById(chartId)
    if (chartContainer) {
      const chartTitle = chartData.title?.text || ''
      const chartSubtitle = chartData.subtitle?.text || ''
      
      const pageContainer = chartContainer.closest('.pdf-page')
      if (pageContainer) {
        const titleElement = pageContainer.querySelector('.title')
        const tooltipElement = pageContainer.querySelector('.tooltip')
        
        if (titleElement && (!titleElement.textContent || titleElement.textContent.trim() === '') && chartTitle) {
          titleElement.textContent = chartTitle
        }
        
        if (tooltipElement && tooltipElement.innerHTML === '' && chartSubtitle) {
          tooltipElement.innerHTML = `<div class="mt-1">${chartSubtitle}</div>`
        }
      }
      
      const { default: registerGroupedCategories } = await import("@xlayer/composables/chartModules/patched-grouped-categories");

      await registerGroupedCategories(HighchartsV12);
      
      HighchartsV12.setOptions({
        lang: {
          decimalPoint: ','
        },
        credits: {
          enabled: false
        },
        chart: {
          animation: false
        },
        yAxis: {
          visible: false
        },
        xAxis: {
          visible: true
        },
        plotOptions: {
          series: {
            animation: false
          }
        }
      })
      
      chartData.subtitle = chartData.subtitle || {}
      chartData.subtitle.text = ''
      chartData.subtitle.enabled = false

      const containerHeight = chartContainer.clientHeight
      chartData.chart = chartData.chart || {}
      chartData.chart.height = containerHeight
      
      chartData.chart.style = chartData.chart.style || {}
      chartData.chart.style.fontSize = '16px'
      
      chartData.title = chartData.title || {}
      chartData.title.text = ''
      chartData.title.enabled = false
      
      chartData.xAxis = chartData.xAxis || {}
      chartData.xAxis.visible = false
      chartData.xAxis.lineWidth = 0
      
      const isBarChart = chartData.chart?.type === 'bar' || chartData.chart?.inverted === true ||
                         (chartData.series && chartData.series.some(s => s.type === 'bar'))

      if (chartData.xAxis) {
        if (Array.isArray(chartData.xAxis)) {
          chartData.xAxis.forEach(axis => {
            if (axis.labels?.style) {
              axis.labels.style.textOverflow = 'none'
              axis.labels.style.width = isBarChart ? '600px' : undefined
              axis.labels.style.fontSize = '16px'
              axis.labels.style.lineClamp = undefined
            }
          })
        } else if (chartData.xAxis.labels?.style) {
          chartData.xAxis.labels.style.textOverflow = 'none'
          chartData.xAxis.labels.style.width = isBarChart ? '1000px' : undefined
          chartData.xAxis.labels.style.fontSize = '16px'
          chartData.xAxis.labels.style.lineClamp = undefined
        }
      }
      
      chartData.yAxis = chartData.yAxis || {}
      chartData.yAxis.visible = false
      chartData.yAxis.lineWidth = 0
      
      if (Array.isArray(chartData.yAxis)) {
        chartData.yAxis.forEach(axis => {
          axis.labels = axis.labels || {}
          axis.labels.style = axis.labels.style || {}
          axis.labels.style.fontSize = '16px'
          axis.labels.style.textOverflow = 'none'
        })
      } else {
        chartData.yAxis.labels = chartData.yAxis.labels || {}
        chartData.yAxis.labels.style = chartData.yAxis.labels.style || {}
        chartData.yAxis.labels.style.fontSize = '16px'
        chartData.yAxis.labels.style.textOverflow = 'none'
      }
      
      if (chartData.legend) {
        chartData.legend.itemStyle = chartData.legend.itemStyle || {}
        
        const isLineChart = chartData.chart?.type === 'line' || 
                           (chartData.series && chartData.series.some(s => s.type === 'line'));
                           
        if (isLineChart) {
          chartData.chart = chartData.chart || {}
          chartData.chart.spacingRight = Math.max(chartData.chart.spacingRight ?? 0, 40)
          
          chartData.legend.layout = 'vertical'
          chartData.legend.align = 'right'      
          chartData.legend.verticalAlign = 'middle'
          
        }
        
        chartData.legend.itemStyle.textOverflow = undefined
        chartData.legend.itemStyle.lineClamp = 5
        chartData.legend.itemStyle.fontSize = '16px'
        chartData.legend.useHTML = false
      }

      if (constructorType === 'mapChart') {
        HighchartsV12.mapChart(chartContainer, chartData)
      } else {
        HighchartsV12.chart(chartContainer, chartData)
      }
    } else {
      console.error("Chart container not found for id:", chartId)
    }
  }

  protected async waitForRenderingComplete(container: HTMLElement) {
    if (container) {
      container.classList.add('chart-rendered');
      console.log("waitForRenderingComplete chart-rendered added")
      document.querySelectorAll('script').forEach(script => {
        script.remove();
      });
    }
  }

  setBasecheckText(warn) {
    const warningElement = this.element.querySelector('#basecheck-warning');
    
    if (warn) {
      warningElement.classList.remove('hidden');
    } else {
      warningElement.classList.add('hidden');
    }
  }
}
